import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Api from '../utils/Api';
import LessonOptions from '../utils/LessonOptions';
import { withBuiltinVersion, escapeCssUrl } from '../utils/CardBackground';
import MyCardDetailModal from '../modal/MyCardDetailModal';
// v0.18.86 (P2-6) — 卡面与弹层**共用**的展示口径 (状态签 / 有效期 / 课时文案 / 课次状态词)。
//   ⚠️ 原来这些函数在本文件里各写一份, 弹层要同样的口径就得再抄一遍 ⇒ 迟早分叉。
//   ⇒ 收进 utils/MyCardView.js (两边都 import), 见该文件头的「一个逻辑一个入口」注释。
import { MC_LEVEL_MAX, expTier, statusesOf, lessonsText } from '../utils/MyCardView';

/**
 * v0.17.80 (辉哥 10/17 拍板) — 「我的卡片」tab
 *
 * ## 权限
 *   页面门 = `scheduler.viewMyCards` (SchedulerIndexPage.TAB_PERMISSIONS 的 `mycards` 项)。
 *   数据门 = 同一个位, 后端行级锁死 `student_id = actor.id` (见 ListMyCardsController 类注释)。
 *   ⇒ 前端**不传**任何 student_id, 后端**也不读** query 参数。两端口径同源, 没有可绕过的口子。
 *
 * ## 视觉: 照抄设计稿 v7 (/tmp/mycards-design-v2/mycards-design-v7.html)
 *   卡壳同源于「学员卡」tab 的卡模板 `.ZS-card-tpl-mobile`, 只换内容:
 *     卡宽 400 / 圆角 14px / 阴影 rgba(0,0,0,.32) 0 2px 10px / 底色 rgb(27,31,38)
 *     背景 `50% 42% cover` / 内边距 `16px 17px 14px`
 *     卡名 16px / 19.2px(1.2) / 600;  大数字 21px / 23.1px(1.1) / 700;  副标题(校区名) 11.5px / 16.1px(1.4)
 *   ⚠️ **无价格** (原 ¥88 那个位置放「15 / 15」)、**无 ⋮ 菜单**。
 *   ⚠️ **不展示 consumed_count** (辉哥 10/17 拍板 3.4: 卡面「15 / 15」已经表达了)。
 *   ⚠️ 紫图标 `#8b5cf6` 压在卡背景图上, 实测对比度 2.40–4.41:1 (WCAG 非文字图形需 3:1)
 *      —— 辉哥**知情接受**, 不许自行改色。本文件只读不写这个色值 (它写在 renderTypeCat 的 JSX 内联里)。
 *   ⚠️ 有效期红字同理: 实测 3.22/2.40 低于 WCAG AA —— 辉哥知情拍板的妥协, 不许改成 AA 合规色。
 *
 * ## 类型/分类 = 「约课管理」tab 的**裸标签**写法 (v7 刚定的)
 *   ⚠️ `.ZS-type-tag` / `.ZS-category-tag` **全站零 CSS** (less/ 搜不到, dist 的 css 里也搜不到) ——
 *      没有底色 / 边框 / 圆角 / padding, 那点绿/紫是 JSX 里 `style="color:..."` 内联写在图标上的。
 *      ⇒ 本文件**刻意不**给它们加任何外壳样式, 也**刻意不**在 less/forum.less 里补规则。
 *      只用到容器 `.ZS-mc-tagchips` (`display:flex; gap:8px`), 照抄 `.ZS-lesson-card-row-types` 同款几何。
 *
 * ## 副标题 (校区名) 的 fallback
 *   ⚠️ `school_id` **可能为 null** (设计稿没画这种情况) ⇒ 副标题必须有 fallback 文案,
 *      不能渲染出空白或 `undefined`。反查不到校区名时写「未设置校区」(见 resolveSchoolName)。
 */

/** 行聚类容差 (按垂直中心聚类, 不用 top —— align-items:center 会骗人)。 */
const MC_LINE_TOL = 8;

/* ⚠️ v0.18.86 — daysLeft / shortDate / expTier / statusesOf / lessonsText / trimNum
   已搬进 `utils/MyCardView.js` (卡面与弹层共用一份, 见该文件头注释)。
   本文件**不再**留副本 —— 留副本就等于埋一个「两边口径迟早分叉」的雷。 */

/** 类型 / 分类 —— 裸标签写法, 与 LessonsTab.js:843-856 逐字同款 (含内联色)。 */
function renderTypeCat(card, level) {
  const typeTag = card.lesson_type_id ? m("span", {
    className: "ZS-type-tag"
  }, m("i", {
    className: "fas fa-tag",
    style: "color:var(--zs-success-22c55e, #22c55e);margin-right:3px;"
  }), LessonOptions.labelFor('type', card.lesson_type_id)) : m("span", {
    className: "ZS-mc-chip-fb"
  }, m("i", {
    className: "fas fa-tag",
    style: "color:var(--zs-text-2-soft-255-255-255-30, rgba(255,255,255,.30));margin-right:3px;"
  }), "\u672A\u8BBE\u7F6E");
  const cats = Array.isArray(card.lesson_category_ids) ? card.lesson_category_ids : [];
  // L2: 超过 2 个就折叠成 [+N] (只减内容不增内容 ⇒ 宽度严格单调递减)
  const shown = level >= 2 && cats.length > 2 ? cats.slice(0, 2) : cats;
  const folded = cats.length - shown.length;
  return m("div", {
    className: "ZS-mc-taggroup"
  }, m("span", {
    className: "ZS-mc-tag-label"
  }, "\u7C7B\u578B"), typeTag, m("span", {
    className: "ZS-mc-tag-div"
  }, "|"), m("div", {
    className: "ZS-mc-taggroup"
  }, m("span", {
    className: "ZS-mc-tag-label"
  }, "\u5206\u7C7B"), m("span", {
    className: "ZS-mc-tagchips"
  }, shown.map(cid => m("span", {
    key: cid,
    className: "ZS-category-tag"
  }, m("i", {
    className: "fas fa-bookmark",
    style: "color:var(--zs-purple, #8b5cf6);margin-right:3px;"
  }), LessonOptions.labelFor('category', cid))), folded > 0 &&
  // 纯展示, 不做点击/长按展开 (只读 tab); title 给全部分类名
  m("span", {
    className: "ZS-category-tag ZS-mc-attr-more",
    title: '还有 ' + folded + ' 个分类: ' + cats.slice(2).map(c => LessonOptions.labelFor('category', c)).join('、')
  }, m("i", {
    className: "fas fa-bookmark",
    style: "color:var(--zs-purple-soft-139-92-246-055, rgba(139,92,246,.55));margin-right:3px;"
  }), "+", folded), cats.length === 0 && m("span", {
    className: "ZS-mc-chip-fb"
  }, m("i", {
    className: "fas fa-bookmark",
    style: "color:var(--zs-text-2-soft-255-255-255-30, rgba(255,255,255,.30));margin-right:3px;"
  }), "\u672A\u5206\u7C7B"))));
}
function renderStatusChips(list) {
  return list.map((s, i) => m("span", {
    key: s.k + i,
    className: 'ZS-mc-chip ' + s.k
  }, m("i", {
    className: 'fas ' + s.icon
  }), s.label));
}

/** 卡面背景: `50% 42% cover` + 底色 #1b1f26 (底色在 less 里兜底, 这里只出 background-image)。 */
function cardBackgroundStyle(card) {
  if (card.bg_image) {
    const url = withBuiltinVersion(card.bg_image);
    return 'background-image: url("' + escapeCssUrl(url) + '");';
  }
  return '';
}

/**
 * 一张卡的完整渲染。`level` = 降级级别 0/1/2。
 *
 * 结构 (照设计稿 v7 的 `.mc-card`):
 *   .ZS-mc-card
 *     .ZS-mc-info
 *       .ZS-mc-name          卡种名 (template.name 的真实格式名, 不编业务含义)
 *       .ZS-mc-desc          副标题 = 校区名 (school_id 可能为 null ⇒ 已有 fallback)
 *       .ZS-mc-meta
 *         课时「15 / 15」
 *         时长 (有值才渲染)
 *         有效期 (带四档色)
 *         [L1] 挪过来的次要状态签
 *       .ZS-mc-date           v0.18.86 (P2-5) 追加的一行小字「体验于 10-24」
 *       .ZS-mc-tagline       margin-top:auto ⇒ 贴底 (高度自适应 + 满/缺字段卡等高)
 *         .ZS-mc-tagline-left  类型/分类 (可折行)
 *         .ZS-mc-tagline-right 状态签 (不折不缩, 贴右缘)
 *     .ZS-mc-open            v0.18.86 (P2-6) 右上角「查看详情 ›」角标 (可点暗示)
 *
 * ## 🔴 v0.18.86 (P2-6) — 卡片变成**真能点**
 *   · `onclick` + `onkeydown` (Enter / Space) ⇒ **键盘可达** (⛔ 不许只做鼠标 onclick)
 *   · `tabindex="0"` + `role="button"` + `aria-label` ⇒ 读屏软件也能读出来
 *   · 右上角「查看详情 ›」角标 + hover/focus 态 ⇒ **看得出来的可点暗示**
 *     (⛔ 不许只靠 `cursor:pointer`)
 *   ⚠️ 卡面**原有**的布局 / 配色 / 标签一个都没动 —— 只**追加**了角标与那一行日期。
 *
 * ## v0.19.16 — 「自主约课」那一行**删掉了**
 *   辉哥 10/28 09:1x 拍板: 入口挪到**已有的**「日历右键『约课』」+「约课管理『+ 新建预约』」
 *   ⇒ 本 tab 回到**纯只读** (⛔ 仍然没有改卡 / 退卡 / 改期, 那些都归前台)。
 */
function renderCard(card, level, schoolName, onOpen) {
  const ex = expTier(card);
  const sts = statusesOf(card);
  const mainSts = sts.slice(0, 1);
  const restSts = sts.slice(1);

  // L1 起: 次要签离开底部行, 挪到有效期那一行
  const onRow = level >= 1 ? mainSts : sts;
  const moved = level >= 1 ? restSts : [];
  const label = card.name + ' 详情';
  return m("div", {
    className: "ZS-mc-card" + (card.bg_image ? ' has-bg' : ''),
    style: cardBackgroundStyle(card),
    "data-level": level,
    "data-card-id": String(card.id),
    role: "button",
    tabindex: "0",
    "aria-label": label,
    onclick: onOpen,
    onkeydown: e => {
      // ⚠️ Enter (13) / Space (32) 都要能触发 —— 只绑 onclick 的话键盘用户点不开。
      //   Space 在 button 上默认会滚动页面 ⇒ 必须 preventDefault。
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 13 || e.keyCode === 32) {
        e.preventDefault();
        if (onOpen) onOpen(e);
      }
    }
  }, m("div", {
    className: "ZS-mc-info"
  }, m("div", {
    className: "ZS-mc-name"
  }, card.name), m("div", {
    className: "ZS-mc-desc"
  }, schoolName), m("div", {
    className: "ZS-mc-meta"
  }, m("span", null, m("span", {
    className: "ZS-mc-lessons"
  }, lessonsText(card))), card.minutes ? m("span", null, m("i", {
    className: "fas fa-stopwatch ZS-mc-t-stopwatch"
  }), m("span", {
    className: "ZS-mc-mini"
  }, card.minutes, " \u5206\u949F")) : null, m("span", null, m("i", {
    className: 'fas fa-calendar-days ' + ex.cls
  }), m("span", {
    className: 'ZS-mc-mini ZS-mc-exp ' + ex.cls
  }, ex.text)), moved.length > 0 && m("span", {
    className: "ZS-mc-meta-status"
  }, renderStatusChips(moved))), renderTrialDateLine(card), m("div", {
    className: "ZS-mc-tagline"
  }, m("div", {
    className: "ZS-mc-tagline-left"
  }, renderTypeCat(card, level)), m("div", {
    className: "ZS-mc-tagline-right"
  }, renderStatusChips(onRow)))), m("span", {
    className: "ZS-mc-open",
    "aria-hidden": "true"
  }, "\u67E5\u770B\u8BE6\u60C5 ", m("i", {
    className: "fas fa-chevron-right"
  })));
}

/**
 * v0.18.86 (P2-5) — 卡面上追加的「体验于 MM-DD」那一行
 *
 * ## 口径 (⛔ 不许显示空值 / 「暂无」/ 任何占位符)
 *   · **只有体验卡**才有「体验日期」这个概念 ⇒ `is_trial_card !== true` ⇒ **整行不渲染**
 *     (10 次卡这类卡 ⇒ 卡面上一个多余的「体验于」比没有更糟)。
 *   · **真的有课次**才渲染 (没有课次 = 没有「哪天上的课」这个事实)。
 *   · 日期 = 该卡名下**第一节课**的上课日期 (后端 `first_lesson_date` 字段,
 *     它已经按 start_at 升序取过第一节课; ⛔ 前端**不**自己再排一次)。
 *   · 日期形状不是 `YYYY-MM-DD` ⇒ **不渲染** (⛔ 宁可少一行, 不许渲染出脏值)。
 */
function renderTrialDateLine(card) {
  if (!card) return null;
  if (card.is_trial_card !== true) return null;
  const lessons = Array.isArray(card.lessons) ? card.lessons : [];
  if (!lessons.length) return null;
  const d = String(card.first_lesson_date || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;

  // 「2026-10-24」→「10-24」 (设计稿/规格给的就是 MM-DD, ⛔ 不是 MM/DD)
  return m("div", {
    className: "ZS-mc-date"
  }, "\u4F53\u9A8C\u4E8E ", d.slice(5));
}

/**
 * 按垂直中心聚类数行 —— **遍历所有后代**, 不只是直接子元素。
 *
 * ⚠️⚠️ v0.17.80 自测抓到的真 bug (第一版只数 `el.children`, 结果降级引擎**整轮不触发**):
 *   `.ZS-mc-tagline-left` 的直接子元素只有两个 `.ZS-mc-taggroup` (类型 / 分类) 和一个
 *   `|` 分隔符。这三个各自都是**不折行**的单行 flex 盒子 (只有 nowrap 的内容),
 *   所以 `lineCount` 恒等于 1 ⇒ `cardFits` 恒 true ⇒ 引擎认为「每一级都放得下」,
 *   L1/L2 一次都不走。
 *   **实际的折行发生在 `.ZS-mc-taggroup` 内部** (它是 `flex-wrap:wrap`) ——
 *   类型名长 + 分类名多时, 「类型 私教 | 分类 单板 双板 公园」会在 taggroup 里折成 2~3 行,
 *   外层 left 容器**高度变高但行数聚类不变**。
 *   ⇒ 判据必须看**所有后代的垂直带**, 否则量的是一个「永远装得下」的假容器。
 *   (这也是本文件与 /tmp/v01780-coder/verify.py 的 lineCount 保持逐字同款的原因 ——
 *    引擎和验收断言必须用**同一个**判据, 否则报告会自相矛盾。)
 */
function lineCount(el) {
  if (!el) return 0;
  const centers = [];
  const all = el.querySelectorAll('*');
  for (let i = 0; i < all.length; i++) {
    const r = all[i].getBoundingClientRect();
    if (!r.height && !r.width) continue;
    const c = r.top + r.height / 2;
    let hit = -1;
    for (let j = 0; j < centers.length; j++) {
      if (Math.abs(centers[j] - c) <= MC_LINE_TOL) {
        hit = j;
        break;
      }
    }
    if (hit < 0) centers.push(c);else centers[hit] = (centers[hit] + c) / 2;
  }
  return centers.length;
}

/**
 * 这一张卡在当前降级级别下「放得下」吗?
 *
 * ⚠️ **量 chip 本体右缘, 不是满宽容器右缘** —— 量容器的话恒为 0, 断言会空转。
 *   (设计稿 v5 的 D4 就栽在这: 断言报 0.00 通过、实际差 150–260px。)
 *
 * 🔴🔴 判「内容装不装得下」时, **绝对不许用 `scrollHeight <= clientHeight`** ——
 *   它在本组件上是**结构性假绿**, 任何宽度都会报「没溢出」。
 *
 *   【哪个组件】`.ZS-mc-card` / `.ZS-mc-info` (本文件 `renderCard` 渲染的那张卡)。
 *   【为什么这个指标不可信】
 *     `.ZS-mc-info` 是 `flex: 1` (撑满锁死卡高) 且 `overflow` 不是 auto,
 *     高度**恒等于**父级给的锁定高度; 子元素又是 `overflow: hidden`。
 *     ⇒ `scrollHeight` 既不会比 `clientHeight` 大, 也不反映「子元素被 flex 压扁了多少」。
 *     v0.17.86 实测: 375 视口下真实内容超出锁定高 **30.36px**,
 *     `.ZS-mc-info` 的 `scrollHeight - clientHeight` 仍然返回 **0** —— 假绿。
 *     (同理 `.ZS-mc-tagline` 的 scrollHeight-clientHeight 也不可信, 别拿它当兜底。)
 *   【正确判据 —— 唯一可信的一条】
 *     **「自然高度 vs 锁死高度」**:
 *       ① 锁死高 = `card.getBoundingClientRect().height` (v0.17.86 锁定的那个盒子);
 *       ② 自然高 = 运行时把 `height:auto` + `aspect-ratio:auto` 注入到这张卡上
 *          (临时注入, **不改文件**), 强制同步布局后量 `getBoundingClientRect().height`,
 *          量完把 inline style 原样还原;
 *       ③ 自然高 ≤ 锁死高 ⇒ 装得下 ✅;  自然高 > 锁死高 ⇒ **装不下**,
 *          超出量就是会被裁掉 / 被 flex 压扁的量。
 *     本函数的 `cardFits` 走的是**横向折行判据** (tagline-left 是否折行),
 *     与上面的**纵向溢出判据**是两件事: 前者决定降到第几级, 后者用来验收「这一级装不装得下」。
 */
function cardFits(cardEl) {
  if (!cardEl) return true;
  const tagline = cardEl.querySelector('.ZS-mc-tagline');
  const left = cardEl.querySelector('.ZS-mc-tagline-left');
  const info = cardEl.querySelector('.ZS-mc-info');
  const right = cardEl.querySelector('.ZS-mc-tagline-right');
  if (!tagline || !info) return true;

  // ① 底部行不能折行
  if (lineCount(left || tagline) > 1) return false;

  // ② 没有孤悬 / 行尾悬空的 `│`
  const scope = left || tagline;
  const kids = Array.prototype.slice.call(scope.children);
  const centerOf = e => {
    const r = e.getBoundingClientRect();
    return r.top + r.height / 2;
  };
  for (let i = 0; i < kids.length; i++) {
    if (!kids[i].classList.contains('ZS-mc-tag-div')) continue;
    if (!kids[i - 1]) continue;
    const d = centerOf(kids[i]);
    const next = kids[i + 1];
    if (Math.abs(d - centerOf(kids[i - 1])) > MC_LINE_TOL) return false;
    if (!next || Math.abs(d - centerOf(next)) > MC_LINE_TOL) return false;
  }

  // ③ 无横向溢出
  if (info.scrollWidth - info.clientWidth > 0) return false;

  // ④ 状态签贴右 —— 量 chip 本体
  if (right) {
    const chips = right.querySelectorAll('.ZS-mc-chip');
    const last = chips[chips.length - 1];
    if (last) {
      const padR = parseFloat(getComputedStyle(info).paddingRight) || 0;
      const contentRight = info.getBoundingClientRect().right - padR;
      if (Math.abs(last.getBoundingClientRect().right - contentRight) > 0.5) return false;
    }
  }
  return true;
}

/**
 * 单张卡 —— 降级引擎在这里跑。
 *
 * ## 🔴 v0.17.86a 修掉的死锁: 在 `oncreate` 里调 `m.redraw.sync()` **提交不了**
 *   v0.17.80 的做法是「在 `oncreate`/`onupdate` 里同步测量 + `m.redraw.sync()` 同步重绘」,
 *   注释里写的是「同一帧 paint 前完成 ⇒ 用户看不到 L0」。**实测证明那个前提不成立。**
 *
 *   ### 探针实测到的真实竞态 (v0.17.86a, 375 移动视口, 引擎内埋点)
 *     首次点进 tab:
 *       oncreate        t=980  level=0  measuredFor=null  elInDoc=true  elW=343  → 判「放不下」
 *       oncreate:redraw t=983  level=1                                                ← 决策做了
 *       ……之后 **再没有 onupdate** …… 最终 DOM 的 data-level = 0 ❌
 *     刷新后进入:
 *       oncreate        t=418  level=0  measuredFor=null  elInDoc=true  elW=343  → 判「放不下」
 *       oncreate:redraw t=420  level=1                                                ← 决策做了
 *       onupdate        t=425  level=1  measuredFor=0                                 ← 迟到的另一次重绘把 L1 真正画了出来
 *       最终 DOM 的 data-level = 1 ✅
 *
 *   ### 成因 (与「布局没稳定」**无关** —— 这点很关键, 排查时极易走偏)
 *     ① 测量时机是好的: 两条路径 `oncreate` 时 `elInDoc=true`、卡宽都是 343px、
 *        `document.fonts.status === 'loaded'` —— 布局**早已稳定**。v0.17.86 注释猜的
 *        「首测发生在布局稳定之前」实测**不成立**, 不必再去加 rAF 等布局。
 *     ② 真正的病根是 **mithril 的重入锁**: `oncreate`/`onupdate` 回调是在
 *        `render()` 内部的 oncreate 队列里、**渲染还没结束时**被调用的
 *        (`for (h=0; h<l.length; h++) l[h]()` 在 `try` 块内)。此时从 `oncreate` 里
 *        再调 `m.redraw.sync()`, 是一次**嵌套重入**, 它不会把这次改级别落到 DOM 上,
 *        而且**不抛异常** —— 于是 `this.level` 已经是 1, 但 `data-level` 还停在 0。
 *     ③ 引擎的状态其实**没坏** (`level`=1 / `measuredFor`=0, 下次 `onupdate` 带着 L1 的
 *        DOM 回来会正确地继续量), 唯独**缺的是一次真的会被采纳的重绘**。
 *        而首点 tab 时**根本没有那次 onupdate**: 刷新路径是靠一次**与本组件无关的、
 *        几毫秒后迟到的全局重绘**（app 水合 / LessonOptions 加载完成等）顺手把 L1 补画出来的;
 *        首点 tab 时这些异步都已完成, 不会再有那次重绘 ⇒ **永远停在 L0**。
 *
 *   ### 本轮怎么消除
 *     把「提交」从 `oncreate` 的**同步重入**里挪出来, 改成:
 *       - `runRound()` 只负责「量 + 定级别 + 记状态」, 不再自己提交;
 *       - 需要提交时调 `requestRender()`: 用 `requestAnimationFrame` 把
 *         `m.redraw()` **推到当前渲染过程之外**再执行 —— rAF 回调运行时
 *         `render()` 早已返回, 重绘一定被采纳;
 *       - 真正的「量 L1 / 量 L2」交给 **`onupdate`** (DOM 已经是新级别时才量),
 *         需要再降一级就再排一个 rAF, 链式走到收敛为止。
 *     ⇒ 不再依赖「有没有一次无关的迟到重绘」, 两条进入路径得到同一结果。
 *     ⚠️ 代价 (如实记): 级别变更要等一帧才落到 DOM ⇒ 极端情况下会看到**一帧**的 L0。
 *        v0.17.80 想要的「零闪烁」在 mithril 的重入锁下本来就拿不到 (它从未真正生效过),
 *        本轮用「一帧」换「一定正确」。
 *
 * ## 幂等: 重跑时**先复位到 L0** 再量
 *   ⚠️ 不复位的话, 重跑时卡片已经是 L1, 量到「放得下」就直接返回 L0 ⇒
 *     「报告级别与 DOM 实际状态脱节」(设计稿 v6 抓到的真 bug: 首跑 [1,1,1] vs 重跑 [0,0,0])。
 *   不复位也意味着引擎任何时候被再次调用都会产出错误状态。
 *
 * ## 收敛: 每级**只减内容不增内容** ⇒ 宽度严格单调递减
 *   终止条件: 放得下就停, 或到 L2 就停 (L2 已是兜底, 再降就要丢信息)。
 */
class MyCardUnit extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    /** 当前降级级别 0/1/2。data-level 也写进 DOM, 供自测脚本直接读。 */
    this.level = 0;
    /** 本轮已经量过的级别 —— 「本级已量过」就是终止判据, 防死循环。 */
    this.measuredFor = null;
    /** 本轮的 key (卡 id + 视口 tick)。key 变了 = 新一轮 ⇒ 必须复位到 L0。 */
    this.roundKeyVal = null;
    this.el = null;
    /** 待提交的重绘 rAF 句柄 (v0.17.86a 新增)。0 = 没有待提交的。 */
    this.renderRaf = 0;
    /** 组件已被卸载 —— rAF 回调里要认这个, 不许对已卸载的组件重绘。 */
    this.gone = false;
  }
  oncreate(vnode) {
    this.el = this.resolveCardEl(vnode);
    this.runRound();
  }
  onupdate(vnode) {
    // 🔴 v0.17.86a: `onupdate` 是**唯一**能拿到「DOM 已经按新级别画出来」的时机,
    //   所以降级链的后续级别 (L1 → L2) 全部在这里续跑。
    this.el = this.resolveCardEl(vnode);
    this.runRound();
  }
  onremove() {
    this.gone = true;
    if (this.renderRaf) {
      cancelAnimationFrame(this.renderRaf);
      this.renderRaf = 0;
    }
    this.el = null;
  }

  /**
   * 请求一次**真的会被采纳**的重绘 (v0.17.86a 新增)。
   *
   * ⚠️ 为什么不能直接 `m.redraw.sync()`: 在 `oncreate` / `onupdate` 里调用它是一次
   *   **嵌套重入**, mithril 不会把这次的改级别落到 DOM 上, 而且**不抛异常**
   *   ⇒ 调用方以为提交成功了, `data-level` 却纹丝不动 (详见本类注释的实测记录)。
   *   `requestAnimationFrame` 回调运行时 `render()` 早已返回、当前渲染过程已结束,
   *   这时再 `m.redraw()` 就是一次普通重绘, 必定生效。
   *
   * 幂等: 已经有待提交的就直接返回, 免得链式降级时排出一串重复重绘。
   */
  requestRender() {
    if (this.renderRaf || this.gone) return;
    this.renderRaf = requestAnimationFrame(() => {
      this.renderRaf = 0;
      if (this.gone) return;
      m.redraw();
    });
  }

  /**
   * 拿到本组件的卡元素。
   *
   * ⚠️⚠️ v0.17.80 自测抓到的第二个真 bug: **不能**写 `vnode.dom.querySelector('.ZS-mc-card')`。
   *   本组件的 `view()` **直接返回**那张卡 (没有外层包装), 所以 `vnode.dom` **就是**
   *   `.ZS-mc-card` 自己。`querySelector` 只在**后代**里找, 找不到元素本身 ⇒ 恒返 null。
   *   而 `cardFits(null)` 的兜底是 `return true` ⇒ **每一级都被判成「放得下」**,
   *   L1/L2 一次都不走, 引擎静默退化成「永远 L0」。
   *   暴露方式: 往引擎里埋探针打 {el, fit, lines}, 发现 el 恒为 null;
   *   只看渲染结果的话, 页面**看不出任何异常** —— 只是那一张双签卡比别的卡高 17.21px。
   *   ⇒ 这个 bug 靠肉眼看页面抓不到, 必须埋探针。
   */
  resolveCardEl(vnode) {
    const dom = vnode && vnode.dom;
    if (!dom) return null;
    if (dom.matches && dom.matches('.ZS-mc-card')) return dom;
    return dom.querySelector ? dom.querySelector('.ZS-mc-card') : null;
  }
  roundKey() {
    return String(this.attrs.card.id) + '|' + String(this.attrs.tick || 0);
  }

  /**
   * 跑一轮降级。**同步**测量, 但**不再自己提交** —— 提交交给 `requestRender()`。
   * 一次 runRound 最多把级别 +1; DOM 被真正重绘后 mithril 会回调 `onupdate`,
   * 那时 DOM 已经是新级别, 引擎接着量 L1 / L2, 链式走到收敛为止。
   */
  runRound() {
    const key = this.roundKey();

    // ① 新一轮 (卡换了 / 视口变了) ⇒ 先复位到 L0。这是幂等的前提。
    if (key !== this.roundKeyVal) {
      this.roundKeyVal = key;
      this.measuredFor = null;
      if (this.level !== 0) {
        this.level = 0;
        this.requestRender(); // 提交也走 rAF 出口, 与下面同一条路
        return; // 下一轮 onupdate 会带着 L0 的 DOM 回来再量
      }
    }

    // ② 本级量过了 ⇒ 终止 (放得下就停 / 到 L2 就停)
    if (this.measuredFor === this.level) return;
    this.measuredFor = this.level;
    if (cardFits(this.el) || this.level >= MC_LEVEL_MAX) return;
    this.level += 1;
    // ⚠️ v0.17.86a: 这里**曾经**是 `m.redraw.sync()`。在 oncreate/onupdate 里它是嵌套重入,
    //   提交不落到 DOM 且不报错 ⇒ 首点 tab 永远停在 L0。改走 rAF 出口 (见 requestRender)。
    this.requestRender();
  }

  /**
   * v0.18.86 (P2-6) — 点卡片 ⇒ 打开「卡片详情」弹层 (方案 B: 卡详情 + 该卡课次)
   *
   * ⚠️ **纯展示**: 弹层的数据全部来自 `this.attrs.card` (列表接口已经带回来了),
   *   ⛔ **不额外发请求**、⛔ **不新开详情端点**。
   * ⚠️ 用全局 `app` (与 SalesTab.openTrialDetail 同款): 本组件没有 `this.app`,
   *   写成 `this.app.modal.show(...)` 表现是「点了完全没反应」且控制台无报错。
   */
  openDetail(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    app.modal.show(MyCardDetailModal, {
      card: this.attrs.card,
      schoolName: this.attrs.schoolName
    });
  }
  view() {
    return renderCard(this.attrs.card, this.level, this.attrs.schoolName, e => this.openDetail(e));
  }
}

/**
 * 校区 id → 名。查不到 (含 school_id 为 null / 反查落空) 一律回落到「未设置校区」。
 * ⚠️ 绝不能返回空串或 undefined —— 设计稿没画 school_id 为 null 的情况,
 *    但库里真的有, 渲染出空白副标题会被当成 bug。
 */
function resolveSchoolName(card, schools) {
  if (card.school_id === null || card.school_id === undefined) return '未设置校区';
  const s = (schools || []).find(x => Number(x.id) === Number(card.school_id));
  return s && s.name ? s.name : '未设置校区';
}
export default class MyCardsTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.cards = [];
    this.loading = true;
    this.error = null;
    /** 视口变化计数 —— 传给每张卡作为「新一轮」的 key, 让引擎在 resize 后复位重测。 */
    this.tick = 0;
    this.onResize = () => {
      this.tick += 1;
      m.redraw();
    };
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    this.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
  }
  load() {
    this.loading = true;
    this.error = null;
    // ⚠️ 不传任何 params —— 后端**不读** query 参数, student_id 恒等于 actor.id。
    //   前端就算被人手改成传别人的 id, 后端也不认 (见 ListMyCardsController 类注释)。
    Api.list('my-cards').then(res => {
      // ⚠️ 数组以外的形状 (403/401 的 {success:false,...}) 一律当空列表 + 显式报错,
      //   绝不能让「权限被挡」渲染成「你还没有课程卡」—— 那是旧端点的静默失败模式。
      if (Array.isArray(res)) {
        this.cards = res;
        this.error = null;
      } else {
        this.cards = [];
        this.error = '你没有「查看我的卡片」权限';
      }
      this.loading = false;
      m.redraw();
    }).catch(e => {
      this.cards = [];
      this.loading = false;
      this.error = (e && e.status) === 403 ? '你没有「查看我的卡片」权限' : '加载失败, 请稍后重试';
      m.redraw();
    });
  }
  view() {
    if (this.loading) {
      return m("div", {
        className: "ZS-mc-wrap"
      }, m(LoadingIndicator, null));
    }
    if (this.error) {
      return m("div", {
        className: "ZS-mc-wrap"
      }, m("div", {
        className: "ZS-mc-empty"
      }, this.error));
    }
    if (!this.cards.length) {
      return m("div", {
        className: "ZS-mc-wrap"
      }, m("div", {
        className: "ZS-mc-empty"
      }, "\u4F60\u8FD8\u6CA1\u6709\u8BFE\u7A0B\u5361"));
    }
    return m("div", {
      className: "ZS-mc-wrap"
    }, m("div", {
      className: "ZS-mc-list"
    }, this.cards.map(c => m(MyCardUnit, {
      key: c.id,
      card: c,
      tick: this.tick,
      schoolName: resolveSchoolName(c, this.attrs.schools)
    }))));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/MyCardsTab', MyCardsTab);